import { useCallback, useEffect, useState } from "react";

import { trpc } from "@blis/trpc/react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DataList,
  DataListItem,
  DataListLabel,
  DataListValue,
} from "@/components/ui/data-list";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { GridList, GridListItem } from "@/components/ui/grid-list";
import { Separator } from "@/components/ui/separator";
import { Sheet } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { Text } from "@/components/ui/text";
import { toast } from "@/components/ui/toast";
import type { TAccessionDetail, TInvoiceInstruction } from "@/lib/types";
import { useDialogProps } from "../detail/state";

export function BillingDialog({ accession }: { accession: TAccessionDetail }) {
  const dialogProps = useDialogProps("editBilling");

  const { data: invoiceInstructions, isLoading } =
    trpc.accession.invoiceInstructions.useQuery(accession.id, {
      enabled: dialogProps.open,
    });

  // Local state for form modifications
  const [localItems, setLocalItems] = useState<TInvoiceInstruction[]>([]);

  // Initialize local state when data loads or changes (due to invalidation)
  useEffect(() => {
    if (invoiceInstructions) {
      setLocalItems(invoiceInstructions);
    }
  }, [invoiceInstructions]);

  const update = trpc.accession.updateBilling.useMutation({
    onSuccess() {
      toast.success("Billing updated successfully");
      dialogProps.onOpenChange?.(false);
    },
    onError(err) {
      toast.error("Error updating billing", {
        description: err.message,
      });
    },
  });

  const allIgnored = localItems.every((item) => item.ignoreInvoice);
  const allInvoiced = localItems.every((item) => item.invoiceId !== null);
  const total = localItems.reduce((prev, curr) => {
    if (curr.ignoreInvoice) {
      return prev;
    }
    return prev + (curr.price || 0);
  }, 0);

  const onSave = useCallback(() => {
    update.mutate({
      accessionId: accession.id,
      items: localItems.map((item) => ({
        id: item.id,
        ignoreInvoice: item.ignoreInvoice,
      })),
    });
  }, [accession.id, localItems, update]);

  return (
    <Sheet
      isOpen={dialogProps.open}
      onOpenChange={dialogProps.onOpenChange}
      side="right"
      classNames={{
        content: "sm:max-w-md",
      }}
    >
      <Dialog>
        <DialogHeader>
          <DialogTitle>Accession billing</DialogTitle>
        </DialogHeader>
        <DialogBody className="flex-1">
          <DataList>
            <DataListItem>
              <DataListLabel>Primary client:</DataListLabel>
              <DataListValue>{accession?.org?.name}</DataListValue>
            </DataListItem>
            <DataListItem>
              <DataListLabel>Bill to:</DataListLabel>
              <DataListValue>
                {accession?.billToOrg?.name || accession?.org?.name}
              </DataListValue>
            </DataListItem>
          </DataList>
          <Separator className="my-4" />
          <div className="flex items-center gap-2 mb-4">
            <Switch
              isSelected={!allIgnored}
              isDisabled={allInvoiced}
              onChange={(selected) => {
                setLocalItems((prevItems) =>
                  prevItems.map((item) => ({
                    ...item,
                    ignoreInvoice: !selected,
                  })),
                );
              }}
            >
              Invoice this accession
            </Switch>
          </div>
          <GridList
            items={localItems}
            selectionMode="multiple"
            selectedKeys={localItems
              .filter((item) => !item.ignoreInvoice)
              .map((item) => item.id.toString())}
            onSelectionChange={(keys) => {
              if (keys === "all") {
                setLocalItems((prevItems) =>
                  prevItems.map((item) => ({
                    ...item,
                    ignoreInvoice: false,
                  })),
                );
              } else {
                setLocalItems((prevItems) =>
                  prevItems.map((item) => ({
                    ...item,
                    ignoreInvoice: !keys.has(item.id.toString()),
                  })),
                );
              }
            }}
            aria-label="invoice-item-list"
          >
            {(item) => (
              <GridListItem
                key={item.id.toString()}
                id={item.id.toString()}
                textValue={item.name || ""}
                isDisabled={item.invoiceId !== null}
              >
                <div className="flex items-center justify-between gap-2 py-1 w-full text-xs min-w-0">
                  <div className="flex flex-col min-w-0">
                    <span className="truncate">{item.name}</span>
                    {item.invoiceId && (
                      <div>
                        <Badge size="sm">Invoiced</Badge>
                      </div>
                    )}
                  </div>
                  <Text muted={item.ignoreInvoice} size="xs">
                    ${item.price?.toFixed(2) || "0.00"}
                  </Text>
                </div>
              </GridListItem>
            )}
          </GridList>
          <div className="flex items-center justify-end gap-2 w-full px-2 my-4">
            <Text bold>Total:</Text>
            <Text>${total.toFixed(2)}</Text>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button isPending={update.isPending} onPress={onSave}>
            Save
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Sheet>
  );
}
